import React from "react";
import { Card, StackItem, StatusDot, Text, VStack } from "@astryxdesign/core";
import type { StatusDotVariant } from "@astryxdesign/core";
import CardLabel from "./CardLabel";
import styles from "./StatCard.module.css";

export type StatCardTone =
  | "neutral"
  | "accent"
  | "positive"
  | "warning"
  | "critical";

export interface StatCardProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  /**
   * Colors both the status dot and the value text.
   * @default 'neutral'
   */
  tone?: StatCardTone;
  /**
   * Definition of the metric, shown in a tooltip behind an info icon next to
   * the label. Requires an Astryx <LayerProvider> ancestor.
   */
  info?: string;
  /** Fixed card width, passed to the underlying Card (e.g. to render several
   * stat cards at a uniform size). */
  width?: number | string;
  /**
   * Vertical placement of the value (and sub caption, if present) within the
   * card's remaining space below the label.
   * - `top`: value sits directly under the label (default, original layout).
   * - `center`: value is centered in the space below the label — useful when
   *   the card is stretched taller than its own content (e.g. alongside a
   *   sibling card in a stretched row).
   * @default 'top'
   */
  valueAlign?: "top" | "center";
}

const TONE_VARIANT: Record<StatCardTone, StatusDotVariant> = {
  neutral: "neutral",
  accent: "accent",
  positive: "success",
  warning: "warning",
  critical: "error",
};

const TONE_DOT_LABEL: Record<StatCardTone, string> = {
  neutral: "Neutral",
  accent: "Highlighted",
  positive: "Positive",
  warning: "Needs attention",
  critical: "Critical",
};

const TONE_VALUE_CLASS: Record<StatCardTone, string> = {
  neutral: styles.valueNeutral,
  accent: styles.valueAccent,
  positive: styles.valuePositive,
  warning: styles.valueWarning,
  critical: styles.valueCritical,
};

/**
 * Single stat tile for a page-level metrics row: a tone dot, an uppercase
 * label, a large value, and an optional supporting caption. Lay several out
 * with StatGrid.
 *
 * Renders via Astryx's StatusDot/Text, so this MUST be rendered inside an
 * Astryx <Theme> scope.
 */
const StatCard = ({
  label,
  value,
  sub,
  tone = "neutral",
  info,
  width,
  valueAlign = "top",
}: StatCardProps): React.ReactElement => {
  const valueBlock = (
    <>
      <div className={[styles.value, TONE_VALUE_CLASS[tone]].join(" ")}>
        {value}
      </div>
      {sub && (
        <Text type="supporting" color="secondary">
          {sub}
        </Text>
      )}
    </>
  );

  const labelRow = (
    <CardLabel
      label={label}
      info={info}
      leading={
        <StatusDot variant={TONE_VARIANT[tone]} label={TONE_DOT_LABEL[tone]} />
      }
    />
  );

  return (
    <Card variant="default" width={width} height="100%">
      {valueAlign === "center" ? (
        <VStack gap={2} height="100%">
          {labelRow}
          <StackItem size="fill">
            <VStack height="100%" vAlign="center" gap={2}>
              {valueBlock}
            </VStack>
          </StackItem>
        </VStack>
      ) : (
        <VStack gap={2}>
          {labelRow}
          {valueBlock}
        </VStack>
      )}
    </Card>
  );
};

export default StatCard;
